/**
 * Email Signup Block Component
 *
 * Handles dynamic padding adjustment for email signup input fields
 * when integrated buttons have variable widths.
 */
class EmailSignupBlock extends HTMLElement {
  constructor() {
    super();
    /** @type {number} */
    this.lastButtonWidth = 0;
    /** @type {ResizeObserver | undefined} */
    this.observer = undefined;

    this.adjustInputPadding = this.adjustInputPadding.bind(this);
  }

  connectedCallback() {
    this.button = this.querySelector('.email-signup__button--integrated');
    this.input = this.querySelector('.email-signup__input');
    this.inputGroup = this.querySelector('.email-signup__input-group');

    const isTextButton = this.button && this.button.classList.contains('email-signup__button--text');

    if (this.button && this.input && this.inputGroup && isTextButton) {
      this.adjustInputPadding();

      this.observer = new ResizeObserver(() => {
        this.adjustInputPadding();
      });
      this.observer.observe(this.button);

      if ('fonts' in document) {
        document.fonts.ready.then(() => {
          this.adjustInputPadding();
        });
      }
    }
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  adjustInputPadding() {
    if (!this.button || !this.inputGroup) return;

    const buttonWidth = /** @type {HTMLElement} */ (this.button).offsetWidth;

    if (buttonWidth !== this.lastButtonWidth) {
      this.lastButtonWidth = buttonWidth;
      /** @type {HTMLElement} */ (this.inputGroup).style.setProperty('--button-actual-width', `${buttonWidth}px`);
    }
  }
}

if (!customElements.get('email-signup-block')) {
  customElements.define('email-signup-block', EmailSignupBlock);
}

(function() {
  const url = new URL(window.location.href);
  if (!url.searchParams.get('customer_posted') || !url.hash) {
    return;
  }

  const formId = url.hash.substring(1);

  const showSuccessMessage = () => {
    const successEl = document.getElementById('Email-signup__message-success-' + formId);
    if (successEl) {
      successEl.style.display = '';
      const input = successEl.closest('form')?.querySelector('.email-signup__input');
      if (input) input.style.display = 'none';
      const button = successEl.closest('form')?.querySelector('.email-signup__button');
      if (button) button.style.display = 'none';
    }
  };

  const scrollToContactForm = () => {
    showSuccessMessage();
    const targetEl = document.getElementById(formId);
    if (!targetEl) return;

    const stickyParent = targetEl.closest('.blog-post__sidebar, [style*="position: sticky"], [style*="position:sticky"]');
    const scrollTarget = stickyParent || targetEl;
    scrollTarget.scrollIntoView({ behavior: 'instant', block: 'center' });
  };

  const hasDynamicContent = () => {
    return document.querySelector('.has-dynamically-added-content') !== null;
  };

  const waitForDynamicContentAndScroll = () => {
    const klaviyoReviewsSelector = '[id*="klaviyo_reviews"]';
    const maxWaitTime = 5000;
    const checkInterval = 100;
    let elapsed = 0;
    let lastKlaviyoCount = 0;
    let stableCount = 0;
    
    const checkKlaviyo = () => {
      const klaviyoElements = document.querySelectorAll(klaviyoReviewsSelector);
      const currentCount = klaviyoElements.length;
      
      if (currentCount > 0) {
        let allLoaded = true;
        klaviyoElements.forEach(el => {
          if (el.offsetHeight === 0 || !el.querySelector('*')) {
            allLoaded = false;
          }
        });
        
        if (allLoaded && currentCount === lastKlaviyoCount) {
          stableCount++;
          if (stableCount >= 3) {
            scrollToContactForm();
            return;
          }
        } else {
          stableCount = 0;
        }
        lastKlaviyoCount = currentCount;
      }
      
      elapsed += checkInterval;
      if (elapsed < maxWaitTime) {
        setTimeout(checkKlaviyo, checkInterval);
      } else {
        scrollToContactForm();
      }
    };
    
    if (document.readyState === 'complete') {
      checkKlaviyo();
    } else {
      window.addEventListener('load', checkKlaviyo, { once: true });
    }
  };

  const init = () => {
    if (hasDynamicContent()) {
      waitForDynamicContentAndScroll();
    } else {
      scrollToContactForm();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
